<template>
  <ProfileLayout>
    <template #header-mobile>
      <span class="uppercase">{{ $t("orderHistory") }}</span>
    </template>
    <template #page-header>
      <span class="uppercase">{{ $t("orderHistory") }}</span>
    </template>
    <template #default>
      <template v-if="isLoading || orders.length">
        <div class="mt-9 grid space-y-13.5 max-md:px-[15px]">
          <img
            v-if="isLoading"
            src="/images/loader.gif"
            alt="Loader"
            class="mx-auto h-10 w-10 object-contain grayscale"
          />
          <template v-else>
            <ProfileOrderCard
              v-for="order in orders"
              :key="order.id"
              :order="order"
              :returns-info-block="returnsInfoBlock"
              @updateOrderData="updateOrderData"
            />
          </template>
        </div>
        <div v-if="isPaginationVisible" class="mb-7 mt-9.5 flex justify-center">
          <UiPagination
            v-model="pages.current"
            :total-pages="pages.total"
            @update:model-value="getOrders"
          />
        </div>
      </template>
      <PagesOrdersNoOrders v-else class="mt-9 px-2.5" />
    </template>
  </ProfileLayout>
</template>

<script setup lang="ts">
import { OrderData } from "ecom-types";
import { FetchError } from "ofetch";

// definePageMeta({
// middleware: ["auth"],
// });

const { $api } = useNuxtApp();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();
const generalStore = useGeneralStore();
const { openSystemDialog } = useSystemDialogs();
const localePath = useLocalePathPolyfill();
const { visitEvent } = useEvents();

const pages = reactive({ current: 1, total: 0, count: 5 });
const orders = ref<OrderData[]>([]);
const isLoading = ref(false);
const returnsInfoBlock = ref<string>("");

const isPaginationVisible = computed(
  () => orders.value?.length && pages.total > 1 && !isLoading.value,
);

setBreadcrumbs([
  { text: "Home", href: "/", disabled: false },
  {
    text: t("myOrderHistory"),
    href: route.path,
    disabled: true,
  },
]);

watch(
  () => generalStore.isAuthenticated,
  (val) => {
    if (val) {
      getOrders();
    }
  },
);

onMounted(() => {
  visitEvent();
  getReturnsInfoBlock();
});

await getData();

async function getOrders() {
  if (process.client) {
    window.scrollTo({ top: 0 });
  }
  isLoading.value = true;

  try {
    const data = await $api.orders.getOrders({
      page: pages.current,
      count: pages.count,
    });
    orders.value = data?.data || [];
    pages.total = data?.meta?.last_page || 0;
  } catch (error) {
    console.log("error - ", error);
  } finally {
    isLoading.value = false;
  }
}

async function getGuestOrder() {
  try {
    const data = await $api.orders.getTrackingOrder({
      email: route.query.email as string,
      number: route.query.number as string,
      billing_zip: route.query.billing_zip as string,
    });
    orders.value = [data.data];
  } catch (error) {
    console.log(error);
    if (error instanceof FetchError) {
      openSystemDialog({
        type: "alert",
        title: `${t("error")}!`,
        text: error?.data?.errors?.[0]?.message || "Wrong data",
      });
    }
  }
}

function updateOrderData(orderData: OrderData) {
  orders.value = orders.value.map((order) =>
    order.id === orderData.id ? orderData : order,
  );
}

async function getData() {
  if (generalStore.isAuthenticated) {
    await getOrders();
  } else if (generalStore.guestOrderInfo) {
    orders.value = [generalStore.guestOrderInfo];
  } else if (
    route.query.email &&
    route.query.number &&
    route.query.billing_zip
  ) {
    await getGuestOrder();
  } else {
    await router.push(localePath("/track-order"));
  }
}

async function getReturnsInfoBlock() {
  try {
    const response = await $api.blocks.getBlock("profile-returns-info", {
      skipErrors: [404, 500],
    });
    returnsInfoBlock.value =
      (response?.data?.currentTranslation as any)?.content?.content || "";
  } catch (error) {
    console.log("Error loading returns info block:", error);
  }
}
</script>
